import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import ModalContainer from 'components/styled/ModalContainer';
import Text from 'components/styled/Text';
import Button from 'components/Button';
import { colors, FILE_TYPES } from '../../../../constants';
import FileItem from '../FileItem';
import { useReduxActions } from 'hooks/useReduxActions';
import { fileLoadingSelector } from 'store/selectors';

const FileInputContainer = styled.div`
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  width: 530px;
  box-shadow: 0px 6px 12px 0px ${colors.navy}12;
`;

const TitleContainer = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid ${colors.grey95};
`;

export const StyledFileInput = styled.input`
  display: none;
`;

export const StyledFileInputLabel = styled.label`
  margin: 24px;
  background-color: ${colors.green90};
  border: 1px ${colors.green50} dashed;
  width: 482px;
  height: 354px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-top: 1px solid ${colors.grey95};
  * {
    margin: 0;
  }
`;

const ButtonsRightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  * {
    cursor: pointer;
  }
`;

const FileInputModal = ({ onClose, rowId, type, recall, model }) => {
  const { t } = useTranslation();
  const actions = useReduxActions();
  const isLoading = useSelector(fileLoadingSelector);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploadStage, setIsUploadStage] = useState(true);

  const handleSelectFile = event => {
    const files = [...event.target.files].map(file => ({
      file,
      id: uuidv4(),
      source: URL.createObjectURL(file),
    }));
    setSelectedFiles([...selectedFiles, ...files]);
    setIsUploadStage(false);
  };

  const handleDropFile = event => {
    const files = [...event.dataTransfer.files].map(file => ({
      file,
      id: uuidv4(),
      source: URL.createObjectURL(file),
    }));
    setSelectedFiles([...selectedFiles, ...files]);
    setIsUploadStage(false);
    event.preventDefault();
  };

  const handleDrag = event => {
    event.preventDefault();
  };

  const handleClearAll = () => {
    setSelectedFiles([]);
    setIsUploadStage(true);
  };

  const handleRemoveFile = id => () => {
    const filesCopy = [...selectedFiles];
    const index = filesCopy.findIndex(file => file.id === id);
    filesCopy.splice(index, 1);
    setSelectedFiles(filesCopy);
    if (!filesCopy.length) {
      setIsUploadStage(true);
    }
  };

  const handleAddMore = () => {
    setIsUploadStage(true);
  };

  const handleUpload = async () => {
    const formData = new FormData();
    selectedFiles.forEach(item => {
      formData.append('files', item.file);
    });
    try {
      await actions.uploadFiles(model, formData, rowId);
      recall();
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (isLoading) {
      toast.loading(t('file.input.modal.toast.loading.message'));
    }
    if (!isLoading) {
      toast.dismiss();
    }
  }, [isLoading]); // eslint-disable-line

  const getInputAccept = () => {
    switch (type) {
      case FILE_TYPES.IMAGE_AND_VIDEO:
        return 'image/x-png,image/jpeg,image/gif,video/mp4,video/x-m4v,video/*';
      case FILE_TYPES.IMAGE:
        return 'image/x-png,image/jpeg,image/gif';
      case FILE_TYPES.VIDEO:
        return 'video/mp4,video/x-m4v,video/*';
      case FILE_TYPES.RAW:
        return 'application/pdf';
      default:
        return 'image/x-png,image/jpeg,image/gif,video/mp4,video/x-m4v,video/*';
    }
  };

  return createPortal(
    <ModalContainer>
      <FileInputContainer>
        <TitleContainer>
          <Text variant="title2">
            {isUploadStage
              ? t('file.input.modal.upload.title')
              : t('file.input.modal.select.title')}
          </Text>
          <MdClose onClick={onClose} fill={colors.navy} />
        </TitleContainer>
        {isUploadStage ? (
          <>
            <StyledFileInputLabel
              onDrop={handleDropFile}
              onDragOver={handleDrag}
              onDragEnter={handleDrag}
              htmlFor="file-upload">
              <Text variant="body-bold">
                {t('file.input.modal.select.file.text')}
              </Text>
              <Text variant="body-small">
                {t('file.input.modal.drag.copy.paste.file')}
              </Text>
            </StyledFileInputLabel>
            <StyledFileInput
              id="file-upload"
              type="file"
              accept={getInputAccept()}
              onChange={handleSelectFile}
              multiple
            />
          </>
        ) : (
          <FilesList>
            {selectedFiles.map(item => (
              <FileItem
                key={item.id}
                item={item}
                onRemoveFile={handleRemoveFile(item.id)}
              />
            ))}
          </FilesList>
        )}
        {!isUploadStage && (
          <ButtonsContainer>
            <Button onClick={handleClearAll} variant="secondary" size="large">
              {t('file.input.modal.clear.all.button')}
            </Button>
            <ButtonsRightContainer>
              <Button variant="light" size="large" onClick={handleAddMore}>
                {t('file.input.modal.add.more.button')}
              </Button>
              <Button onClick={handleUpload} variant="primary" size="large">
                {t('file.input.modal.upload.button')}
              </Button>
            </ButtonsRightContainer>
          </ButtonsContainer>
        )}
      </FileInputContainer>
    </ModalContainer>,
    document.body,
  );
};

FileInputModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  recall: PropTypes.func.isRequired,
  rowId: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(FILE_TYPES)).isRequired,
  model: PropTypes.string.isRequired,
};

export default FileInputModal;
